import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

/** Thin wrapper around the native `<dialog>` element.
 *
 * Native dialog handles modal behavior — backdrop, focus trap, Esc to
 * close — without a popover library. The wrapper just plumbs `open`
 * to the imperative `showModal()` / `close()` calls and surfaces the
 * dismiss-on-backdrop-click pattern that vanilla `<dialog>` lacks.
 *
 * The `<dialog>` element is rendered into a portal at `document.body`
 * so callers can mount this anywhere — including inside a `<tbody>` —
 * without violating table-DOM nesting rules. React used to warn about
 * the latter; some browsers actually reparent the node, breaking the
 * modal. The portal sidesteps both.
 */
export function Dialog({
  open,
  onClose,
  children,
  className,
  width = 'max-w-md',
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** Tailwind max-w-* token applied to the inner panel. */
  width?: string;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Catch the native `close` event (Esc, form `method="dialog"`) so the
  // parent state stays in sync when the user dismisses via keyboard.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handler = () => onClose();
    dialog.addEventListener('close', handler);
    return () => dialog.removeEventListener('close', handler);
  }, [onClose]);

  // SSR-safety guard. Vite + TanStack Router are CSR today so
  // `document` is always defined; the typeof check is a cheap insurance
  // policy for any future SSR / pre-render path.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <dialog
      ref={ref}
      onClick={(e) => {
        // Backdrop click: the click target is the <dialog> itself rather
        // than any descendant element. Close on outside-click without
        // adding a separate overlay component.
        if (e.target === e.currentTarget) onClose();
      }}
      // Stretch the <dialog> across the viewport so positioning is owned
      // by an inner flex container rather than the UA's default
      // `<dialog>` layout (which is inconsistent across browsers when
      // `w-full` is applied — Chromium ends up top-aligned, Safari
      // centered). The dialog itself becomes the scrim; the inner panel
      // is centered via flex.
      className="m-0 h-screen max-h-screen w-screen max-w-none bg-transparent p-0 backdrop:bg-brand-navy/40 backdrop:backdrop-blur-sm"
    >
      <div className="flex h-full w-full items-center justify-center p-4">
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'rounded-md border border-border bg-card text-card-foreground shadow-lg',
            'w-full',
            width,
            'p-5',
            className,
          )}
        >
          {children}
        </div>
      </div>
    </dialog>,
    document.body,
  );
}

export function DialogHeader({ children }: { children: ReactNode }) {
  return <div className="mb-3 flex flex-col gap-1">{children}</div>;
}

export function DialogTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-base font-semibold tracking-tight text-brand-navy">{children}</h3>
  );
}

export function DialogDescription({ children }: { children: ReactNode }) {
  return <p className="text-sm text-brand-slate">{children}</p>;
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return <div className="mt-4 flex items-center justify-end gap-2">{children}</div>;
}
